import {FILTER_KEYS, FILTER_VALUES, TRUE_FALSE_FILTER_VALUES} from '../constants/filter-constants'
import {Strings} from '../constants/strings'
import type {FilterQuery} from '../filter-query'
import {
  type FilterBlock,
  type FilterConfig,
  type FilterKey,
  FilterProviderType,
  type FilterSuggestion,
  type IndexedBlockValueItem,
  type SuppliedFilterProviderOptions,
  ValidationTypes,
} from '../types'
import {
  caseInsensitiveStringCompare,
  getFilterValue,
  getUnescapedFilterValue,
  singleFilterProviderOptions,
} from '../utils'
import {getEmptyValueValidation, getMultiValueValidation, StaticFilterProvider} from './static'

export class BooleanFilterProvider extends StaticFilterProvider {
  constructor(
    filter: FilterKey,
    filterValues: FilterSuggestion[] = TRUE_FALSE_FILTER_VALUES,
    options?: SuppliedFilterProviderOptions,
  ) {
    super(filter, filterValues, {
      ...singleFilterProviderOptions,
      ...options,
      filterTypes: {...singleFilterProviderOptions.filterTypes, ...options?.filterTypes},
    })
    this.type = FilterProviderType.Boolean
  }

  override validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
    config: FilterConfig,
  ): IndexedBlockValueItem[] {
    return values.map((v, i) => {
      if (!this.allowsFilterValues()) {
        return {
          ...v,
          valid: false,
          validations: [{type: ValidationTypes.InvalidValue, message: Strings.filterValuesUnsupported(this.key)}],
        }
      }
      if (!this.options.filterTypes.multiValue && i > 0) {
        return getMultiValueValidation(v, this.key)
      }

      const extractedValue = getUnescapedFilterValue(v.value)

      if (!extractedValue) {
        return getEmptyValueValidation(v, this.key)
      }

      const typeValidity =
        this.filterValues.find(fv => {
          return (
            getFilterValue(fv.value) === extractedValue ||
            (config?.aliasMatching && fv.aliases?.includes(extractedValue))
          )
        }) !== undefined

      const suggestion = this.filterValues.find(fv =>
        caseInsensitiveStringCompare(getFilterValue(fv.value) ?? '', extractedValue),
      )
      if (suggestion) {
        // We explicitly set the value to the extracted value here because the suggestion value may be different
        return {...v, ...suggestion, valid: true, value: v.value}
      } else {
        return {...v, valid: typeValidity}
      }
    })
  }
}

export class ArchivedFilterProvider extends BooleanFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.archived, FILTER_VALUES.archived, {
      ...options,
      filterTypes: {...options?.filterTypes, multiValue: false, valueless: false},
    })
  }
}

export class DraftFilterProvider extends BooleanFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {multiValue: false, valueless: false}}
    super(FILTER_KEYS.draft, FILTER_VALUES.draft, {
      ...options,
      filterTypes: {...defaultOptions.filterTypes, ...options?.filterTypes},
    })
    this.type = FilterProviderType.Boolean
  }
}
